import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCategories } from '../../services/marketplace';

// ─── Inline stroked SVG icons (Lucide / Feather style — never emojis) ───────
const ChevronLeft = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const CloseIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const PlusCircle = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="16" />
    <line x1="8" y1="12" x2="16" y2="12" />
  </svg>
);

const CloudOff = ({ size = 34 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.61 16.95A5 5 0 0 0 18 10h-1.26a8 8 0 0 0-7.05-6M5 5a8 8 0 0 0 4 15h9a5 5 0 0 0 1.7-.3" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// Drill-down category picker over the deep parts taxonomy
// (department -> category -> part_type), rendered as a web modal panel.
// Mirrors the app's MarketplaceCategoryPicker:
//   - one getCategories() fetch; tree built from the flat list via parentId
//   - tapping a node with children drills in; tapping a leaf selects it
//   - "Browse all in <name>" selects the current non-leaf node
// onChange receives the chosen node ({ id, slug, departmentSlug, name, ... }).
export default function MarketplaceCategoryPicker({ value, onChange, onClose }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [stack, setStack] = useState([]); // breadcrumb of nodes

  const loadCats = useCallback(() => {
    setLoading(true);
    setError(false);
    getCategories()
      .then((d) => {
        if (Array.isArray(d?.categories)) setAll(d.categories);
        else { setAll([]); setError(true); }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCats(); }, [loadCats]);

  const parentId = stack.length ? stack[stack.length - 1].id : null;
  const current = stack.length ? stack[stack.length - 1] : null;

  // children-by-parent map, built once from the flat list.
  const childrenOf = useMemo(() => {
    const map = new Map();
    for (const c of all) {
      const key = c.parentId == null ? 'root' : c.parentId;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    for (const list of map.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [all]);

  const items = childrenOf.get(parentId == null ? 'root' : parentId) || [];

  const select = (node) => {
    // Carry the full node so departmentSlug survives (gates wheel facets).
    onChange(node);
    if (onClose) onClose();
  };

  const handleRow = (node) => {
    const hasChildren = (childrenOf.get(node.id) || []).length > 0;
    if (hasChildren) setStack((st) => [...st, node]);
    else select(node);
  };

  const back = () => setStack((st) => st.slice(0, -1));

  const stop = (e) => e.stopPropagation();

  return (
    <div className="mp-modal-backdrop" onClick={onClose}>
      <div className="mp-modal" onClick={stop} role="dialog" aria-modal="true">
        <div className="mp-modal__header">
          {stack.length ? (
            <button type="button" className="mp-modal__icon-btn" onClick={back} aria-label="Back">
              <ChevronLeft />
            </button>
          ) : (
            <span style={{ width: 32 }} />
          )}
          <span className="mp-modal__title">{current ? current.name : 'All categories'}</span>
          <button type="button" className="mp-modal__icon-btn" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        {current ? (
          <button type="button" className="mp-cat-select-all" onClick={() => select(current)}>
            Browse all in {current.name}
          </button>
        ) : null}

        <div className="mp-modal__body">
          {loading ? (
            <div className="mp-state"><div className="mp-spinner" /></div>
          ) : error || all.length === 0 ? (
            <div className="mp-state mp-state--error">
              <CloudOff />
              <h3>Couldn’t load categories</h3>
              <p>The marketplace backend isn’t reachable. Make sure the server is running.</p>
              <button type="button" className="mp-retry-btn" onClick={loadCats}>Retry</button>
            </div>
          ) : (
            items.map((node) => {
              const hasChildren = (childrenOf.get(node.id) || []).length > 0;
              const isSelected = value && value.id === node.id;
              return (
                <button
                  type="button"
                  key={node.id}
                  className="mp-cat-row"
                  onClick={() => handleRow(node)}
                >
                  <span style={isSelected ? { color: '#fff', fontWeight: 700 } : undefined}>
                    {node.name}
                  </span>
                  {hasChildren
                    ? <ChevronRight />
                    : <PlusCircle />}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
